import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Admin CRUD schemas for website marketing tags (themes / collections).
 * One physical table (`website_marketing_tags`) discriminated by `type`,
 * simplified to just `type / name / description`. Create/edit is limited to
 * theme + collection; the list filter accepts any string so legacy types
 * (e.g. "season") remain visible.
 */

export const MARKETING_TAG_TYPES = ['theme', 'collection'] as const;
export const MarketingTagTypeSchema = z.enum(MARKETING_TAG_TYPES);
export type MarketingTagType = z.infer<typeof MarketingTagTypeSchema>;

export const WebsiteMarketingTagListItemSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  type: z.string(),
  isActive: z.boolean(),
  createdAt: IsoDateTimeSchema,
});
export type WebsiteMarketingTagListItem = z.infer<typeof WebsiteMarketingTagListItemSchema>;

export const WebsiteMarketingTagDetailSchema = WebsiteMarketingTagListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type WebsiteMarketingTagDetail = z.infer<typeof WebsiteMarketingTagDetailSchema>;

export const WebsiteMarketingTagListQuerySchema = PaginationQuerySchema.extend({
  type: z.string().trim().min(1).optional(),
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type WebsiteMarketingTagListQuery = z.infer<typeof WebsiteMarketingTagListQuerySchema>;

export const WebsiteMarketingTagListResponseSchema = paginatedResponseSchema(
  WebsiteMarketingTagListItemSchema,
);
export type WebsiteMarketingTagListResponse = z.infer<typeof WebsiteMarketingTagListResponseSchema>;

const MarketingTagCoreFieldsSchema = z.object({
  type: MarketingTagTypeSchema,
  name: z.string().trim().min(1, 'Name is required').max(200),
  description: z.string().trim().max(2000).nullish(),
  // Optional (not .default) to keep the Zod input/output types identical, which
  // react-hook-form's resolver typing requires. The API service defaults it to
  // true (active) on create.
  isActive: z.boolean().optional(),
});

export const CreateWebsiteMarketingTagRequestSchema = MarketingTagCoreFieldsSchema;
export type CreateWebsiteMarketingTagRequest = z.infer<
  typeof CreateWebsiteMarketingTagRequestSchema
>;

export const UpdateWebsiteMarketingTagRequestSchema = MarketingTagCoreFieldsSchema.partial();
export type UpdateWebsiteMarketingTagRequest = z.infer<
  typeof UpdateWebsiteMarketingTagRequestSchema
>;
